// components/StoreList.tsx
"use client";

import Link from "next/link";
import type { StoreData } from "@/types/store";

interface StoreListProps {
  stores: StoreData[];
  selectedLetter: string; // required - always has a letter
  categoryName?: string | null; // optional - category name when filtered by category
  totalStores?: number; // total count of stores (for showing "View all" link)
}

export default function StoreList({ stores, selectedLetter, categoryName, totalStores = 0 }: StoreListProps) {
  const currentLetter = selectedLetter || "a";
  const isCategoryFilter = !!categoryName;
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  // Stores are already filtered by letter or category on the server, so no need to filter again
  const filteredStores = stores;
  
  // Check if there are more than 45 stores (only show "View all" if there are more)
  const hasMoreStores = totalStores > 45 && stores.length === 45;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      {/* Header */}
      <header className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 text-center">
          {isCategoryFilter ? `Stores in ${categoryName} Category` : "Browse All Stores"}
        </h1>
      </header>

      {/* Store Grid with Letter Display */}
      {filteredStores.length > 0 ? (
        <div className="flex gap-8">
          {/* Large Letter Display - Only show when filtering by letter */}
          {!isCategoryFilter && (
            <div className="hidden md:flex items-start pt-2">
              <span className="text-8xl md:text-9xl font-bold text-gray-300 leading-none">
                {currentLetter === "0-9" ? "#" : currentLetter.toUpperCase()}
              </span>
            </div>
          )}

          {/* Stores in 3 Columns */}
          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[0, 1, 2].map((colIndex) => {
              const storesInColumn = filteredStores.filter(
                (_, index) => index % 3 === colIndex
              );
              
              return (
                <ul key={colIndex} className="space-y-2 list-none">
                  {storesInColumn.map((store) => (
                    <li key={store._id}>
                      <Link
                        href={`/stores/${store.slug}`}
                        className="text-gray-900 hover:text-purple-700 text-lg transition-colors"
                      >
                        {store.name} Coupons
                      </Link>
                    </li>
                  ))}
                </ul>
              );
            })}
          </div>
        </div>
      ) : (
        <p className="text-gray-500 italic text-center">
          {isCategoryFilter
            ? `No stores found in ${categoryName} category`
            : currentLetter
            ? `No stores found starting with ${currentLetter.toUpperCase()}`
            : "No stores available"}
        </p>
      )}

      {/* View All Link - Only show when filtering by letter and there are more than 45 stores */}
      {!isCategoryFilter && hasMoreStores && (
        <div className="mt-8 text-center">
          <Link
            href={`/stores?letter=${currentLetter}&all=true`}
            className="text-purple-700 hover:text-purple-900 text-sm md:text-base underline"
          >
            View all stores starting with {currentLetter === "0-9" ? "0-9" : currentLetter.toUpperCase()}
          </Link>
        </div>
      )}

      {/* Alphabetical Navigation */}
      <section className="border-t border-gray-200 pt-8 mt-12">
        <div className="flex flex-wrap justify-center gap-3 md:gap-4">
          {alphabet.map((letter) => (
            <Link
              key={letter}
              href={`/stores?letter=${letter.toLowerCase()}`}
              className={`font-medium text-lg transition-colors ${
                currentLetter?.toLowerCase() === letter.toLowerCase()
                  ? "text-purple-900 font-bold"
                  : "text-purple-700 hover:text-purple-900"
              }`}
            >
              {letter}
            </Link>
          ))}
          <Link
            href="/stores?letter=0-9"
            className={`font-medium text-lg transition-colors ${
              currentLetter === "0-9"
                ? "text-purple-900 font-bold"
                : "text-purple-700 hover:text-purple-900"
            }`}
          >
            #
          </Link>
        </div>
      </section>
    </div>
  );
}

